import {
  domainConfig,
  getPublicDomainConfig,
  normalizeDomain,
  resolveDomainPathLocale,
} from "@/lib/domainConfig";

export type LocaleItem = {
  locale: string;
  name: string;
  region: string;
  domain: string;
  completeSupport: boolean;
  hreflang: string;
  visiblePrefix: string;
  isDefaultLocale: boolean;
};

const LANGUAGE_NAME_BY_LOCALE: Record<string, string> = {
  ar: "العربية",
  bg: "Български",
  cs: "Čeština",
  da: "Dansk",
  de: "Deutsch",
  el: "Ελληνικά",
  en: "English",
  es: "Español",
  fi: "Suomi",
  fr: "Français",
  hi: "हिन्दी",
  hr: "Hrvatski",
  hu: "Magyar",
  it: "Italiano",
  ja: "日本語",
  ko: "한국어",
  nl: "Nederlands",
  no: "Norsk",
  pl: "Polski",
  pt: "Português",
  ro: "Română",
  ru: "Русский",
  sk: "Slovenčina",
  sl: "Slovenščina",
  sr: "Српски",
  sv: "Svenska",
  tr: "Türkçe",
  uk: "Українська",
  zh: "简体中文",
};

export const localesData: LocaleItem[] = Object.entries(domainConfig).flatMap(
  ([domain, config]) =>
    config.supportedLocales.map((locale) => ({
      locale,
      name: LANGUAGE_NAME_BY_LOCALE[locale] || locale,
      region: config.marketCountryCode,
      domain,
      completeSupport: true,
      hreflang: config.hreflangByLocale[locale] || locale,
      visiblePrefix: locale === config.defaultLocale ? "" : `/${locale}`,
      isDefaultLocale: locale === config.defaultLocale,
    })),
);

export const getUniqueLocales = () => {
  const seen = new Set<string>();

  return localesData
    .filter((item) => {
      if (seen.has(item.locale)) return false;
      seen.add(item.locale);
      return true;
    })
    .map((item) => ({ code: item.locale, name: item.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const uniqueLanguages = getUniqueLocales();

export function getLocaleTarget({
  domain,
  locale,
}: {
  domain?: string | null;
  locale: string;
}): LocaleItem {
  const normalizedDomain = normalizeDomain(domain);
  const config = getPublicDomainConfig(normalizedDomain);

  if (!config) {
    return getFallbackLocaleItem();
  }

  const resolvedLocale = config.supportedLocales.includes(locale)
    ? locale
    : config.defaultLocale;

  return {
    locale: resolvedLocale,
    name: LANGUAGE_NAME_BY_LOCALE[resolvedLocale] || resolvedLocale,
    region: config.marketCountryCode,
    domain: normalizedDomain,
    completeSupport: true,
    hreflang: config.hreflangByLocale[resolvedLocale] || resolvedLocale,
    visiblePrefix:
      resolvedLocale === config.defaultLocale ? "" : `/${resolvedLocale}`,
    isDefaultLocale: resolvedLocale === config.defaultLocale,
  };
}

export function getLocaleTargetsForDomain(domain?: string | null) {
  const normalizedDomain = normalizeDomain(domain);
  const config = getPublicDomainConfig(normalizedDomain);

  if (!config) return [getFallbackLocaleItem()];

  return config.supportedLocales.map((locale) =>
    getLocaleTarget({ domain: normalizedDomain, locale }),
  );
}

/**
 * Compatibility helper.
 * If called with a locale only, returns the primary selectable target for that locale.
 * Prefer getLocaleTarget({ domain, locale }) when domain matters.
 */
export function getLocaleData(lang?: string): LocaleItem {
  if (lang) {
    return (
      localesData.find((item) => item.locale === lang) ||
      getFallbackLocaleItem()
    );
  }

  if (typeof window === "undefined") {
    return getFallbackLocaleItem();
  }

  const resolution = resolveDomainPathLocale({
    domain: window.location.hostname,
    pathname: window.location.pathname,
  });

  return getLocaleTarget({
    domain: resolution.domain,
    locale: resolution.locale,
  });
}

/**
 * Deprecated compatibility helper.
 * Domain alone is not enough for multilingual domains like doomum.ae.
 * Prefer getLocaleTarget({ domain, locale }).
 */
export function getLocaleDataFromDomain(domain: string): LocaleItem {
  const config = getPublicDomainConfig(domain);

  return getLocaleTarget({
    domain,
    locale: config?.defaultLocale || "en",
  });
}

export function getLocaleDataFromDomainAndLocale({
  domain,
  locale,
}: {
  domain: string;
  locale: string;
}) {
  return getLocaleTarget({ domain, locale });
}

function getFallbackLocaleItem(): LocaleItem {
  return {
    locale: "en",
    name: "English",
    region: "GLOBAL",
    domain: "doomum.com",
    completeSupport: true,
    hreflang: "en",
    visiblePrefix: "",
    isDefaultLocale: true,
  };
}

export const WORLD_LABEL_BY_LOCALE: Record<string, string> = {
  // English
  en: "World",

  // Arabic
  ar: "العالم",

  // Bulgarian
  bg: "Свят",

  // Czech
  cs: "Svět",

  // Danish
  da: "Verden",

  // German
  de: "Welt",

  // Greek
  el: "Κόσμος",

  // Spanish
  es: "Mundo",

  // Finnish
  fi: "Maailma",

  // French
  fr: "Monde",

  // Hindi
  hi: "दुनिया",

  // Croatian
  hr: "Svijet",

  // Hungarian
  hu: "Világ",

  // Italian
  it: "Mondo",

  // Japanese
  ja: "世界",

  // Korean
  ko: "세계",

  // Dutch
  nl: "Wereld",

  // Norwegian
  no: "Verden",

  // Polish
  pl: "Świat",

  // Portuguese
  pt: "Mundo",

  // Romanian
  ro: "Lume",

  // Russian
  ru: "Мир",

  // Slovak
  sk: "Svet",

  // Slovenian
  sl: "Svet",

  // Serbian
  sr: "Свет",

  // Swedish
  sv: "Världen",

  // Turkish
  tr: "Dünya",

  // Ukrainian
  uk: "Світ",

  // Chinese (Simplified)
  zh: "世界",
};
